/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Action } from '@reduxjs/toolkit';
import { useCallback, useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import type {
  FeaturesState,
  NotificationScopeEvent,
  PluginManager,
  ScopeEventMap,
  ScopeName,
} from '@opengeoweb/plugin-interface';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import type { CoreAppStore, layerTypes } from '@opengeoweb/store';
import {
  genericActions,
  genericSelectors,
  layerActions,
  mapActions,
  useUpdateFeatureSyncLayers,
} from '@opengeoweb/store';
import { useDispatchOnScopeEvent } from '../../hooks';
import { pluginsActions } from '../../store';

const toFeatureLayerEntry = (
  layer: FeaturesState['featureLayers'][number],
): layerTypes.FeatureLayer | null => {
  if (!layer.id) {
    return null;
  }

  return {
    geojson: layer.featureCollection,
  };
};

export const toFeatureSyncLayersById = (
  bridgeState: FeaturesState | undefined,
): Record<string, layerTypes.FeatureLayer> => {
  if (!bridgeState?.featureLayers?.length) {
    return {};
  }

  return bridgeState.featureLayers.reduce<
    Record<string, layerTypes.FeatureLayer>
  >((acc, layer) => {
    const featureLayer = toFeatureLayerEntry(layer);
    if (!featureLayer || !layer.id) {
      return acc;
    }

    return {
      ...acc,
      [layer.id]: featureLayer,
    };
  }, {});
};

interface UsePluginToReduxOptions {
  pluginBridge: PluginManager;
  sourceId: string;
  hasScope: (scope: ScopeName) => boolean;
  hostPanelId?: string;
  pluginId?: string;
}

export const usePluginToRedux = ({
  pluginBridge,
  sourceId,
  hasScope,
  hostPanelId,
  pluginId,
}: UsePluginToReduxOptions): void => {
  const [pluginFeatureState, setPluginFeatureState] = useState<
    FeaturesState | undefined
  >(() => pluginBridge.getState('features') as FeaturesState | undefined);
  const [isPluginFeatureStateInitialized, setIsPluginFeatureStateInitialized] =
    useState(() => pluginBridge.getState('features') !== undefined);
  const featureSyncLayersById = useMemo(
    () => toFeatureSyncLayersById(pluginFeatureState),
    [pluginFeatureState],
  );
  const store = useStore<CoreAppStore>();

  useUpdateFeatureSyncLayers(
    featureSyncLayersById,
    hasScope('features') && isPluginFeatureStateInitialized
      ? sourceId
      : undefined,
  );

  const toSnackbarMessage = (
    event: NotificationScopeEvent,
  ): snackbarTypes.SnackbarMessage => {
    let prefix = '';
    if (event.severity === 'ERROR') {
      prefix = 'Error: ';
    } else if (event.severity === 'WARNING') {
      prefix = 'Warning: ';
    }

    return {
      type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
      message: `${prefix}${event.content}`,
    };
  };

  const timeEventHandler = useCallback(
    (event: ScopeEventMap['time']): Action =>
      genericActions.setTime({
        sourceId,
        origin: 'PluginBridge',
        value: { currentTime: event.time },
      }),
    [sourceId],
  );

  const animationEventHandler = useCallback(
    (event: ScopeEventMap['animation']): Action | Action[] | null => {
      switch (event.type) {
        case 'setAnimationStartTime':
          return mapActions.setAnimationStartTime({
            mapId: sourceId,
            animationStartTime: event.time,
          });
        case 'setAnimationEndTime':
          return mapActions.setAnimationEndTime({
            mapId: sourceId,
            animationEndTime: event.time,
          });
        case 'setAnimationTimeSpan':
          return [
            mapActions.setAnimationStartTime({
              mapId: sourceId,
              animationStartTime: event.startTime,
            }),
            mapActions.setAnimationEndTime({
              mapId: sourceId,
              animationEndTime: event.endTime,
            }),
          ];
        default:
          return null;
      }
    },
    [sourceId],
  );

  const featureEventHandler = useCallback(
    (event: ScopeEventMap['features']): Action | Action[] | null => {
      setIsPluginFeatureStateInitialized(true);

      switch (event.type) {
        case 'addLayer': {
          const featureLayerKey =
            event.layer.id ?? `pluginLayerId-${crypto.randomUUID()}`;
          setPluginFeatureState((current) => {
            const existingLayers = current?.featureLayers ?? [];
            const remainingLayers = existingLayers.filter(
              (layer) => layer.id !== featureLayerKey,
            );

            return {
              featureLayers: [
                ...remainingLayers,
                {
                  id: featureLayerKey,
                  title: event.layer.title ?? featureLayerKey,
                  featureCollection: event.layer.featureCollection,
                },
              ],
            };
          });
          return null;
        }
        case 'removeLayer':
          setPluginFeatureState((current) => ({
            featureLayers:
              current?.featureLayers.filter(
                (layer) => layer.id !== event.layerId,
              ) ?? [],
          }));
          return null;
        case 'setLayerFeatures':
          setPluginFeatureState((current) => {
            const existingLayers = current?.featureLayers ?? [];
            const matchingLayer = existingLayers.find(
              (layer) => layer.id === event.layerId,
            );
            const remainingLayers = existingLayers.filter(
              (layer) => layer.id !== event.layerId,
            );

            return {
              featureLayers: [
                ...remainingLayers,
                {
                  id: event.layerId,
                  title: matchingLayer?.title ?? event.layerId,
                  featureCollection: event.featureCollection,
                },
              ],
            };
          });
          return null;
        default:
          return null;
      }
    },
    [],
  );

  const featureSelectionEventHandler = useCallback(
    (event: ScopeEventMap['featureSelection']): Action[] => {
      const state = store.getState();
      const linkedLayers =
        genericSelectors.selectFeatureSyncLayersForLinkedMaps(state, sourceId);
      const layersById = state.layers?.byId ?? {};
      const selectedFeatureIdsByLayerId = new Map(
        event.featureLayers.map((layer) => [
          layer.id,
          layer.selectedFeatureIds,
        ]),
      );

      return linkedLayers.flatMap((layer) => {
        const layerId = layer.id;
        if (!layerId) {
          return [];
        }
        const featureLayerKey =
          layersById[layerId]?.featureSync?.featureLayerKey ?? layerId;
        const selectedFeatureIds =
          selectedFeatureIdsByLayerId.get(featureLayerKey) ??
          selectedFeatureIdsByLayerId.get(layerId) ??
          [];

        return [
          layerActions.setSelectedFeatures({
            layerId,
            selectedFeatureIds,
          }),
        ];
      });
    },
    [sourceId, store],
  );

  const featureHoverEventHandler = useCallback(
    (event: ScopeEventMap['featureHover']): Action[] => {
      const state = store.getState();
      const linkedLayers =
        genericSelectors.selectFeatureSyncLayersForLinkedMaps(state, sourceId);
      const layersById = state.layers?.byId ?? {};

      return linkedLayers.flatMap(({ id }) => {
        if (!id) {
          return [];
        }

        const featureLayerKey =
          layersById[id]?.featureSync?.featureLayerKey ?? id;

        if (
          featureLayerKey !== event.featureLayerId &&
          id !== event.featureLayerId
        ) {
          return [];
        }

        return [
          layerActions.setHoveredFeatures({
            layerId: id,
            hoveredFeatureIds: event.hoveredFeatureIds,
          }),
        ];
      });
    },
    [sourceId, store],
  );

  // Only an overlay plugin has a window to show or hide. A view plugin's
  // setPluginVisibility only changes its own runtime state.
  const runtimeEventHandler = useCallback(
    (event: ScopeEventMap['runtime']): Action | null => {
      if (event.type !== 'setPluginVisibility' || !hostPanelId || !pluginId) {
        return null;
      }
      const action =
        event.pluginVisibility === 'hidden'
          ? pluginsActions.hideOverlayPlugin
          : pluginsActions.showOverlayPlugin;
      return action({ panelId: hostPanelId, pluginId });
    },
    [hostPanelId, pluginId],
  );

  const notificationEventHandler = useCallback(
    (event: NotificationScopeEvent): Action | null => {
      if (event.type !== 'showNotification') {
        return null;
      }
      return snackbarActions.openSnackbar(toSnackbarMessage(event));
    },
    [],
  );

  useDispatchOnScopeEvent(
    pluginBridge,
    'time',
    timeEventHandler,
    hasScope('time'),
  );
  useDispatchOnScopeEvent(
    pluginBridge,
    'animation',
    animationEventHandler,
    hasScope('animation'),
  );
  useDispatchOnScopeEvent(
    pluginBridge,
    'features',
    featureEventHandler,
    hasScope('features'),
  );
  useDispatchOnScopeEvent(
    pluginBridge,
    'featureSelection',
    featureSelectionEventHandler,
    hasScope('featureSelection'),
  );
  useDispatchOnScopeEvent(
    pluginBridge,
    'featureHover',
    featureHoverEventHandler,
    hasScope('featureHover'),
  );
  useDispatchOnScopeEvent(
    pluginBridge,
    'notification',
    notificationEventHandler,
    hasScope('notification'),
  );
  useDispatchOnScopeEvent(
    pluginBridge,
    'runtime',
    runtimeEventHandler,
    hasScope('runtime') && !!hostPanelId && !!pluginId,
  );
};
